// Article Review — open a draft, edit fields, manage context cards,
// publish or retract. The screen the editor spends the most time in.
//
// Route: /article/:id
//
// What this screen does:
//   - Edit title / summary / body / image URL
//   - Refine with AI (free-text instruction) and Rewrite with current prompt
//   - Show classification (sub-threads + arcs + confidence) read-only
//   - Attach / reorder / remove context cards, incl. AI-drafted new ones
//   - Show cited sources (web_search footnotes) and the source list, with
//     single and bulk detach
//   - Action bar: Save Draft / Publish / Retract
//
// Still missing: full sub-thread reassignment UI. The endpoints exist
// (POST/DELETE /news/editor/articles/:id/assign) but nothing here calls them.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Textarea } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { EntityGraphPanel } from "../../components/EntityGraphPanel";
import { AddContextCardSheet } from "../../components/AddContextCardSheet";
import { api } from "../../lib/api";

interface SubThread {
  id: string;
  name: string;
  description: string | null;
  primary_arc_id: string;
  confidence: number | null;
  source: string;
}

interface Arc {
  id: string;
  name: string;
  description: string | null;
}

interface Source {
  id: string;
  source_name: string | null;
  source_url: string;
  source_author: string | null;
  source_published_at: string | null;
  original_title: string;
  original_summary: string | null;
}

interface ContextCard {
  id: string;
  slug: string;
  category: string;
  title: string;
  body: string;
  external_url: string | null;
  external_label: string | null;
  display_order: number;
  attached_by: string;
}

interface Citation {
  url: string;
  title: string | null;
  source: string | null;
}

interface ArticleResponse {
  id: string;
  title: string;
  summary: string | null;
  body: string | null;
  image_url: string | null;
  status: string;
  drafted_at: string;
  drafted_by: string;
  published_at: string | null;
  retracted_at: string | null;
  classification_confidence: number | null;
  sub_threads: SubThread[];
  arcs: Arc[];
  sources: Source[];
  context_cards: ContextCard[];
  citations?: Citation[];
}

export default function ArticleReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [article, setArticle] = useState<ArticleResponse | null>(null);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Bulk source-detach: keep selection separate from the loaded article so
  // the user can keep selecting across re-fetches if they want, but reset
  // it when the article id changes.
  const [selectedSources, setSelectedSources] = useState<Set<string>>(
    new Set(),
  );
  const [detaching, setDetaching] = useState(false);

  // Refine-with-AI state
  const [refineInstruction, setRefineInstruction] = useState("");
  const [refining, setRefining] = useState(false);

  // Add-context-card sheet
  const [showAddCard, setShowAddCard] = useState(false);

  // Bumped after anything that changes the body or publishes. Publishing
  // kicks off extraction in the background, so the panel needs to re-poll to
  // pick it up; a body edit makes the last extraction stale.
  const [graphRefresh, setGraphRefresh] = useState(0);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const a = await api<ArticleResponse>(`/news/editor/articles/${id}`);
      setArticle(a);
      setTitle(a.title);
      setSummary(a.summary ?? "");
      setBody(a.body ?? "");
      setImageUrl(a.image_url ?? "");
    } catch (e: any) {
      setError(e.message ?? "Failed to load article");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Reset bulk selection when the article id changes — selecting on one
  // draft and navigating to another shouldn't carry the selection over.
  useEffect(() => {
    setSelectedSources(new Set());
  }, [id]);

  async function save(targetStatus?: "draft" | "published" | "retracted") {
    if (!id) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const payload: Record<string, unknown> = {
        title: title.trim(),
        summary: summary.trim() || null,
        body,
        image_url: imageUrl.trim() || null,
      };
      if (targetStatus) payload.status = targetStatus;

      await api(`/news/editor/articles/${id}`, {
        method: "PUT",
        body: payload,
      });

      const noticeText =
        targetStatus === "published"
          ? "Published — article is live in the news feed."
          : targetStatus === "retracted"
            ? "Retracted — article is hidden from readers."
            : "Saved.";
      setNotice(noticeText);
      await load();
      setGraphRefresh((v) => v + 1);
    } catch (e: any) {
      setError(e.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function rewriteFromSources() {
    if (!id) return;
    const hasSources = (article?.sources.length ?? 0) > 0;
    const message = hasSources
      ? "Rewrite this article from its original sources using the current prompt? Title, summary, and body will be replaced. This cannot be undone (but you can keep editing afterward)."
      : "This article has no linked sources. Claude will use the existing body as context, re-research the story via web search, and rewrite under the current prompt. Title, summary, and body will be replaced. This cannot be undone (but you can keep editing afterward).";
    if (!confirm(message)) {
      return;
    }
    setRewriting(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api<{
        title: string;
        body: string;
        summary: string | null;
      }>(`/news/editor/articles/${id}/rewrite`, { method: "POST" });
      // Push the new content into the form fields so the editor sees the
      // result immediately (and can keep tweaking).
      setTitle(r.title);
      setBody(r.body);
      if (r.summary !== undefined) setSummary(r.summary ?? "");
      setNotice("Rewrote with current prompt — review and save.");
      await load();
      setGraphRefresh((v) => v + 1);
    } catch (e: any) {
      setError(e.message ?? "Rewrite failed");
    } finally {
      setRewriting(false);
    }
  }

  async function detachSource(sourceId: string, sourceTitle: string) {
    if (!id) return;
    if (
      !confirm(
        `Remove "${truncate(sourceTitle, 80)}" from this article's sources? It will be re-queued for clustering on the next pipeline run.`,
      )
    ) {
      return;
    }
    try {
      await api(`/news/editor/articles/${id}/sources/${sourceId}`, {
        method: "DELETE",
      });
      await load();
    } catch (e: any) {
      setError(e.message ?? "Failed to detach source");
    }
  }

  function toggleSourceSelection(sourceId: string) {
    setSelectedSources((prev) => {
      const next = new Set(prev);
      if (next.has(sourceId)) next.delete(sourceId);
      else next.add(sourceId);
      return next;
    });
  }

  function selectAllSources() {
    if (!article) return;
    setSelectedSources(new Set(article.sources.map((s) => s.id)));
  }

  function clearSelection() {
    setSelectedSources(new Set());
  }

  async function detachSelected() {
    if (!id || selectedSources.size === 0 || detaching) return;
    const count = selectedSources.size;
    if (
      !confirm(
        `Remove ${count} source${count === 1 ? "" : "s"} from this article? They will be re-queued for clustering on the next pipeline run.`,
      )
    ) {
      return;
    }
    setDetaching(true);
    setError(null);
    try {
      const r = await api<{ detached: number; skipped: number }>(
        `/news/editor/articles/${id}/sources/detach`,
        {
          method: "POST",
          body: { source_ids: Array.from(selectedSources) },
        },
      );
      setNotice(
        `Removed ${r.detached} source${r.detached === 1 ? "" : "s"}.${
          r.skipped > 0 ? ` (${r.skipped} skipped)` : ""
        }`,
      );
      clearSelection();
      await load();
    } catch (e: any) {
      setError(e.message ?? "Failed to detach sources");
    } finally {
      setDetaching(false);
    }
  }

  async function reorderCard(cardId: string, direction: "up" | "down") {
    if (!id || !article) return;
    const cards = [...article.context_cards].sort(
      (a, b) => a.display_order - b.display_order,
    );
    const idx = cards.findIndex((c) => c.id === cardId);
    if (idx === -1) return;
    const swapWith = direction === "up" ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= cards.length) return;

    const reordered = [...cards];
    [reordered[idx], reordered[swapWith]] = [
      reordered[swapWith],
      reordered[idx],
    ];

    setError(null);
    try {
      await api(`/news/editor/articles/${id}/context-cards/reorder`, {
        method: "PATCH",
        body: {
          order: reordered.map((c, i) => ({
            context_card_id: c.id,
            display_order: i,
          })),
        },
      });
      await load();
    } catch (e: any) {
      setError(e.message ?? "Failed to reorder");
    }
  }

  async function refineWithAI() {
    if (!id) return;
    if (!refineInstruction.trim()) {
      setError("Tell Claude what to change.");
      return;
    }
    setRefining(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api<{
        title: string;
        body: string;
        summary: string | null;
      }>(`/news/editor/articles/${id}/refine`, {
        method: "POST",
        body: { instruction: refineInstruction.trim() },
      });
      setTitle(r.title);
      setBody(r.body);
      if (r.summary !== undefined) setSummary(r.summary ?? "");
      setRefineInstruction("");
      setNotice("Refined — review and save when ready.");
      setGraphRefresh((v) => v + 1);
    } catch (e: any) {
      setError(e.message ?? "Refine failed");
    } finally {
      setRefining(false);
    }
  }

  async function removeContextCard(cardId: string) {
    if (!id) return;
    if (!confirm("Remove this context card from the article?")) return;
    try {
      await api(`/news/editor/articles/${id}/context-cards/${cardId}`, {
        method: "DELETE",
      });
      await load();
    } catch (e: any) {
      setError(e.message ?? "Failed to remove card");
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Spinner size={20} />
      </div>
    );
  }
  if (!article) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <CardSubtle style={{ color: "var(--danger)" }}>
            {error ?? "Article not found"}
          </CardSubtle>
          <Button
            variant="secondary"
            onClick={() => navigate("/dashboard")}
            style={{ marginTop: 12 }}
          >
            Back to dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate("/dashboard")}
          >
            ← Dashboard
          </Button>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: "12px 0 4px" }}>
            Article Review
          </h1>
          <CardSubtle>
            <StatusPill status={article.status} /> · drafted{" "}
            {formatRelative(article.drafted_at)} by {article.drafted_by}
            {article.classification_confidence != null &&
              ` · classification ${(article.classification_confidence * 100).toFixed(0)}%`}
          </CardSubtle>
        </div>
      </div>

      <div style={styles.grid}>
        {/* LEFT: editable fields */}
        <div>
          <Card style={{ marginBottom: 16 }}>
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={500}
              style={{ marginBottom: 16 }}
            />
            <Label>Summary</Label>
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              maxLength={2000}
              rows={3}
              style={{ marginBottom: 16 }}
            />
            <Label>Image URL</Label>
            <Input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://..."
              style={{ marginBottom: 8 }}
            />
            {imageUrl && (
              <img
                src={imageUrl}
                alt=""
                style={{
                  maxWidth: "100%",
                  maxHeight: 200,
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border)",
                }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            )}
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <Label>Body</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={24}
              style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 13,
                lineHeight: 1.6,
              }}
            />
          </Card>

          {/* Refine with AI */}
          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Refine with AI</CardTitle>
            <CardSubtle style={{ marginBottom: 8 }}>
              Tell Claude exactly what to change or add. Example: "Name the
              specific colonial-era treaties referenced — the 1929
              Anglo-Egyptian Treaty and the 1959 Nile Waters Agreement — with
              their proper context."
            </CardSubtle>
            <Textarea
              value={refineInstruction}
              onChange={(e) => setRefineInstruction(e.target.value)}
              rows={4}
              placeholder="What should Claude revise or add?"
              disabled={refining}
              style={{ marginBottom: 8 }}
            />
            <Button
              variant="secondary"
              onClick={refineWithAI}
              disabled={
                refining || saving || rewriting || !refineInstruction.trim()
              }
            >
              {refining ? "Refining…" : "Refine with AI"}
            </Button>
          </Card>

          {/* Action bar */}
          <Card>
            <div style={styles.actionBar}>
              <Button onClick={() => save()} disabled={saving || rewriting}>
                {saving ? "Saving…" : "Save draft"}
              </Button>
              {article.status !== "published" && (
                <Button
                  onClick={() => save("published")}
                  disabled={saving || rewriting}
                >
                  Publish
                </Button>
              )}
              {article.status === "published" && (
                <Button
                  variant="danger"
                  onClick={() => {
                    if (
                      confirm(
                        "Retract this article? It will be hidden from readers.",
                      )
                    ) {
                      void save("retracted");
                    }
                  }}
                  disabled={saving || rewriting}
                >
                  Retract
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={rewriteFromSources}
                disabled={saving || rewriting}
                title={
                  (article?.sources.length ?? 0) > 0
                    ? "Re-synthesize from the original sources using the current prompt"
                    : "Re-research via web search and rewrite under the current prompt"
                }
              >
                {rewriting ? "Rewriting…" : "Rewrite with current prompt"}
              </Button>
              {notice && (
                <span style={{ color: "var(--success)", fontSize: 13 }}>
                  {notice}
                </span>
              )}
              {error && (
                <span style={{ color: "var(--danger)", fontSize: 13 }}>
                  {error}
                </span>
              )}
            </div>
          </Card>
        </div>

        {/* RIGHT: classification + context cards + sources */}
        <div>
          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Classification</CardTitle>
            {article.sub_threads.length === 0 ? (
              <CardSubtle>(unassigned)</CardSubtle>
            ) : (
              article.sub_threads.map((st) => {
                const arc = article.arcs.find(
                  (a) => a.id === st.primary_arc_id,
                );
                return (
                  <div key={st.id} style={styles.classificationRow}>
                    <div style={{ fontWeight: 600 }}>{st.name}</div>
                    <CardSubtle>
                      {arc?.name ?? "(orphan)"}
                      {st.confidence != null &&
                        ` · ${(st.confidence * 100).toFixed(0)}% (${st.source})`}
                    </CardSubtle>
                  </div>
                );
              })
            )}
          </Card>

          {/* Extraction proposes; nothing reaches the graph until it is
              approved here. Sits next to Classification because both
              answer 'what is this article about'. */}
          <EntityGraphPanel articleId={id!} refreshKey={graphRefresh} />

          <Card style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <CardTitle style={{ marginBottom: 0 }}>
                Context Cards ({article.context_cards.length})
              </CardTitle>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddCard(true)}
              >
                + Add card
              </Button>
            </div>
            {article.context_cards.length === 0 ? (
              <CardSubtle>None attached.</CardSubtle>
            ) : (
              [...article.context_cards]
                .sort((a, b) => a.display_order - b.display_order)
                .map((card, i, arr) => (
                  <div key={card.id} style={styles.cardRow}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{card.title}</div>
                      <CardSubtle>
                        {card.category} · attached by {card.attached_by}
                      </CardSubtle>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <button
                        onClick={() => reorderCard(card.id, "up")}
                        disabled={i === 0}
                        aria-label="Move up"
                        title="Move up"
                        style={{
                          ...styles.reorderBtn,
                          opacity: i === 0 ? 0.3 : 1,
                          cursor: i === 0 ? "default" : "pointer",
                        }}
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => reorderCard(card.id, "down")}
                        disabled={i === arr.length - 1}
                        aria-label="Move down"
                        title="Move down"
                        style={{
                          ...styles.reorderBtn,
                          opacity: i === arr.length - 1 ? 0.3 : 1,
                          cursor: i === arr.length - 1 ? "default" : "pointer",
                        }}
                      >
                        ▼
                      </button>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => removeContextCard(card.id)}
                    >
                      Remove
                    </Button>
                  </div>
                ))
            )}
          </Card>

          {article.citations && article.citations.length > 0 ? (
            <Card style={{ marginBottom: 16 }}>
              <CardTitle>Sources cited ({article.citations.length})</CardTitle>
              <CardSubtle style={{ marginBottom: 8 }}>
                Inline footnotes from Claude's web_search. Body has [N] markers
                indexing into this list. Readers see the same list at the bottom
                of the article (non-clickable).
              </CardSubtle>
              {article.citations.map((c, i) => (
                <div key={i} style={styles.citationRow}>
                  <span style={styles.citationNumber}>[{i + 1}]</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {c.source ? (
                      <div style={{ fontWeight: 600 }}>{c.source}</div>
                    ) : null}
                    {c.title ? (
                      <div style={{ fontSize: 13, fontStyle: "italic" }}>
                        {c.title}
                      </div>
                    ) : null}
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-muted)",
                        wordBreak: "break-all",
                        marginTop: 2,
                      }}
                    >
                      {c.url}
                    </div>
                  </div>
                </div>
              ))}
            </Card>
          ) : null}

          <Card>
            <CardTitle>Sources ({article.sources.length})</CardTitle>
            {article.sources.length === 0 ? (
              <CardSubtle>No sources linked.</CardSubtle>
            ) : (
              <>
                <div style={styles.sourceToolbar}>
                  {selectedSources.size > 0 ? (
                    <>
                      <span
                        style={{ fontSize: 13, color: "var(--text-secondary)" }}
                      >
                        {selectedSources.size} selected
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={detachSelected}
                        disabled={detaching}
                      >
                        {detaching
                          ? "Removing…"
                          : `Remove ${selectedSources.size}`}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={clearSelection}
                        disabled={detaching}
                      >
                        Clear
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={selectAllSources}
                    >
                      Select all
                    </Button>
                  )}
                </div>
                {article.sources.map((src) => {
                  const checked = selectedSources.has(src.id);
                  return (
                    <div key={src.id} style={styles.sourceRow}>
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSourceSelection(src.id)}
                          style={styles.sourceCheckbox}
                          aria-label={`Select ${src.original_title}`}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <a
                            href={src.source_url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontWeight: 600 }}
                          >
                            {src.original_title}
                          </a>
                          <CardSubtle>
                            {src.source_name ?? "Unknown source"}
                            {src.source_author && ` · ${src.source_author}`}
                            {src.source_published_at &&
                              ` · ${formatRelative(src.source_published_at)}`}
                          </CardSubtle>
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            detachSource(src.id, src.original_title)
                          }
                          title="Remove this source from the article and re-queue it for clustering"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </Card>
        </div>
      </div>

      <AddContextCardSheet
        open={showAddCard}
        articleId={id ?? ""}
        onClose={() => setShowAddCard(false)}
        onAttached={() => {
          setNotice("Context card attached.");
          void load();
        }}
      />
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const colorByStatus: Record<string, string> = {
    draft: "var(--text-muted)",
    in_review: "var(--accent)",
    published: "var(--success)",
    retracted: "var(--danger)",
  };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        background: "rgba(255,255,255,0.06)",
        color: colorByStatus[status] ?? "var(--text-secondary)",
      }}
    >
      {status}
    </span>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function formatRelative(ts: string | null | undefined): string {
  if (!ts) return "—";
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    marginBottom: 20,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
    gap: 16,
  },
  actionBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  classificationRow: {
    padding: "8px 0",
    borderBottom: "1px solid var(--border)",
  },
  cardRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 0",
    borderBottom: "1px solid var(--border)",
  },
  reorderBtn: {
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: 4,
    color: "var(--text-secondary)",
    fontSize: 10,
    width: 24,
    height: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  sourceRow: {
    padding: "8px 0",
    borderBottom: "1px solid var(--border)",
  },
  sourceToolbar: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    paddingBottom: 8,
    marginBottom: 4,
    borderBottom: "1px solid var(--border)",
  },
  sourceCheckbox: {
    marginTop: 4,
    flexShrink: 0,
    accentColor: "var(--accent)",
    cursor: "pointer",
  },
  citationRow: {
    display: "flex",
    gap: 8,
    padding: "8px 0",
    borderBottom: "1px solid var(--border)",
    fontSize: 13,
  },
  citationNumber: {
    color: "var(--text-secondary)",
    fontWeight: 600,
    width: 28,
    flexShrink: 0,
  },
};
